import { Check, Copy, LoaderCircle } from 'lucide-react';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { AutosaveStatus } from '../lib/autosave.js';
import './action-feedback.css';

export function SaveFeedback({
  status,
  error,
  onRetry,
}: {
  status: AutosaveStatus;
  error?: string;
  onRetry(): void;
}) {
  const alert = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (status !== 'Error') return;
    const active = document.activeElement;
    if (
      active instanceof HTMLElement &&
      active.matches('input,textarea,select,[contenteditable="true"]')
    )
      return;
    alert.current?.focus({ preventScroll: true });
  }, [status]);
  if (status === 'Saved' || status === 'Conflict') return null;
  if (status === 'Error')
    return (
      <div
        ref={alert}
        tabIndex={-1}
        className="dui-save-feedback dui-save-feedback--error"
        role="alert"
      >
        <div>
          <strong>Could not save</strong>
          <p>Your edits are still here. {error || 'Try again.'}</p>
        </div>
        <button type="button" className="shoot-button shoot-button--outline" onClick={onRetry}>
          Retry save
        </button>
      </div>
    );
  return (
    <span className="dui-save-feedback" role="status">
      {status === 'Saving' ? <LoaderCircle size={15} aria-hidden="true" /> : null}
      {status === 'Saving' ? 'Saving…' : 'Unsaved changes'}
    </span>
  );
}

/** Acknowledges confirmed revision changes, never initial hydration or a dirty draft. */
export function SaveConfirmation({
  status,
  revision,
  dirty,
}: {
  status: AutosaveStatus;
  revision: number;
  dirty: boolean;
}) {
  const previous = useRef({ revision, status });
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (
      revision !== previous.current.revision &&
      previous.current.status === 'Saving' &&
      status === 'Saved' &&
      !dirty
    )
      setVisible(true);
    previous.current = { revision, status };
    if (status !== 'Saved' || dirty) setVisible(false);
  }, [revision, status, dirty]);
  useEffect(() => {
    if (!visible) return;
    const timer = window.setTimeout(() => setVisible(false), 2500);
    return () => window.clearTimeout(timer);
  }, [visible, revision]);
  return visible ? (
    <div className="dui-save-confirmation" role="status">
      <Check size={16} aria-hidden="true" />
      Saved
    </div>
  ) : null;
}

export function CopyAction({
  value,
  label,
  className = 'shoot-button shoot-button--outline shoot-button--icon',
  copyText = (text) => navigator.clipboard.writeText(text),
}: {
  value: string;
  label: string;
  className?: string;
  copyText?(value: string): Promise<void>;
}) {
  const [state, setState] = useState<'idle' | 'copying' | 'copied' | 'error'>('idle');
  const request = useRef(0);
  const busy = useRef(false);
  const messageId = useId();
  useEffect(() => {
    setState('idle');
    busy.current = false;
    ++request.current;
    return () => {
      ++request.current;
    };
  }, [value]);
  useEffect(() => {
    if (state !== 'copied') return;
    const timer = window.setTimeout(() => setState('idle'), 2200);
    return () => window.clearTimeout(timer);
  }, [state]);
  const copy = async () => {
    if (busy.current) return;
    busy.current = true;
    const attempt = ++request.current;
    setState('copying');
    try {
      await copyText(value);
      if (attempt === request.current) setState('copied');
    } catch {
      if (attempt === request.current) setState('error');
    } finally {
      if (attempt === request.current) busy.current = false;
    }
  };
  return (
    <span className="dui-copy-action">
      <button
        type="button"
        className={className}
        aria-label={`Copy ${label.toLowerCase()}`}
        title={state === 'copied' ? 'Copied' : `Copy ${label.toLowerCase()}`}
        aria-describedby={state === 'error' ? messageId : undefined}
        aria-busy={state === 'copying'}
        disabled={state === 'copying'}
        data-copy-state={state}
        onClick={() => void copy()}
      >
        {state === 'copied' ? (
          <Check size={15} aria-hidden="true" />
        ) : state === 'copying' ? (
          <LoaderCircle size={15} aria-hidden="true" />
        ) : (
          <Copy size={15} aria-hidden="true" />
        )}
      </button>
      <span className="sr-only" role="status">
        {state === 'copied' ? `${label} copied` : state === 'copying' ? 'Copying…' : ''}
      </span>
      {state === 'error' ? (
        <span id={messageId} className="dui-copy-action__error" role="alert">
          Could not copy {label.toLowerCase()}. Try again.
        </span>
      ) : null}
    </span>
  );
}
